import React from "react";
import { Navigate } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import { Permission } from "../../types/roles";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  requireAll?: boolean; // If true, user must have ALL permissions; if false, user must have ANY permission
  adminOnly?: boolean; // If true, only admin users can access
  fallback?: React.ReactNode;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredPermissions,
  requireAll = false,
  adminOnly = false,
  fallback,
  redirectTo = "/dashboard",
}) => {
  const { checkPermission, checkAnyPermission, isAdmin } = usePermissions();

  // Check admin access if adminOnly is true
  if (adminOnly && !isAdmin) {
    return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
  }

  // Check single permission
  if (requiredPermission) {
    const hasAccess = checkPermission(requiredPermission);
    if (!hasAccess) {
      return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
    }
  }

  // Check multiple permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    let hasAccess = false;

    if (requireAll) {
      // User must have ALL permissions
      hasAccess = requiredPermissions.every((perm) => checkPermission(perm));
    } else {
      // User must have ANY of the permissions
      hasAccess = checkAnyPermission(requiredPermissions);
    }

    if (!hasAccess) {
      return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
